import {
  Button,
  MenuItem,
  Modal,
  ModalContent,
  ModalOverlay,
  ModalHeader,
  ModalCloseButton,
  useDisclosure,
  ModalFooter,
  useToast,
} from '@chakra-ui/react';
import React from 'react';

import { ShareFundingComponentFragmentGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { colors } from '~/packages/common-frontend/colors';
import { Typography } from '~/packages/common-frontend/components/Typography';
import {
  errorToastAdminConfig,
  successToastConfig,
} from '~/packages/common-frontend/constants';
import {
  useMarkLenderShareComponentFundTransferDateMutation,
  GetResolvedLoanByIdDocumentGQLType,
} from '~/packages/loans/requests/loans.gql.generated';
import {
  GetDailyAccountActivityDocumentGQLType,
  GetDailyResolvedAggregateLineItemEntriesDocumentGQLType,
} from '~/packages/transactions/requests/transactions.gql.generated';

interface UndoLenderShareComponentFundedModalProps {
  fundingComponent: ShareFundingComponentFragmentGQLType;
  modalControls: ReturnType<typeof useDisclosure>;
}

const UndoLenderShareComponentFundedModal = ({
  fundingComponent,
  modalControls,
}: UndoLenderShareComponentFundedModalProps) => {
  const toast = useToast();

  const [
    markLenderShareComponentFundTransferDate,
    { loading: isLoading },
  ] = useMarkLenderShareComponentFundTransferDateMutation({
    refetchQueries: [
      GetResolvedLoanByIdDocumentGQLType,
      GetDailyAccountActivityDocumentGQLType,
      GetDailyResolvedAggregateLineItemEntriesDocumentGQLType,
    ],
  });

  const undoMarkFunded = async (): Promise<void> => {
    const { fundingRequestComponentId, lenderShareId } = fundingComponent;

    try {
      await markLenderShareComponentFundTransferDate({
        variables: {
          lenderShareId,
          fundingRequestComponentId,
        },
      });
      toast({
        ...successToastConfig,
        title: `Lender share marked as funded.`,
      });
    } catch (error) {
      toast({
        ...errorToastAdminConfig,
        title:
          'If you are unable to resolve the error, contact the Finley support team for assistance.',
      });
    }
  };

  return (
    <Modal {...modalControls}>
      <ModalOverlay />
      <ModalContent padding="20px">
        <ModalHeader>
          <Typography variant="section-subtitle">Undo Mark Funded</Typography>
          <Typography variant="base-muted">
            Undoing &quot;Mark Funded&quot; will remove the current Lender
            Funded Date and may impact accounting records. Ensure accuracy
            before proceeding.
          </Typography>
        </ModalHeader>
        <ModalCloseButton />
        <ModalFooter gap="0.5rem" mt="1rem">
          <Button
            colorScheme="v2-aurora"
            onClick={modalControls.onClose}
            variant="secondary"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            colorScheme="v2-aurora"
            onClick={async () => {
              await undoMarkFunded();
              modalControls.onClose();
            }}
            isLoading={isLoading}
          >
            Apply Changes
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

interface UndoLenderShareComponentFundedMenuItemProps {
  fundingComponent: ShareFundingComponentFragmentGQLType;
}

export const UndoLenderShareComponentFundedMenuItem = ({
  fundingComponent,
}: UndoLenderShareComponentFundedMenuItemProps) => {
  const modalControls = useDisclosure();

  return (
    <MenuItem as="div">
      <Button
        variant="unstyled"
        fontWeight={400}
        color={colors.gray[800]}
        onClick={modalControls.onOpen}
        isDisabled={!fundingComponent.fundTransferDate}
      >
        Undo Mark Funded
      </Button>
      <UndoLenderShareComponentFundedModal
        fundingComponent={fundingComponent}
        modalControls={modalControls}
      />
    </MenuItem>
  );
};
